import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Badge,
  Spinner,
  Alert,
  ListGroup,
  ProgressBar,
} from 'react-bootstrap';
import {
  FaArrowLeft,
  FaCalendarAlt,
  FaClock,
  FaUser,
  FaPhone,
  FaEnvelope,
  FaFileContract,
  FaCalendarCheck,
  FaCalendarDay,
  FaNotesMedical,
  FaHardHat,
  FaDollarSign,
  FaRulerCombined,
  FaSync,
  FaCheckCircle,
  FaTimesCircle,
  FaBuilding,
  FaClipboardCheck,
  FaChartLine,
  FaFileAlt,
  FaInfoCircle,
  FaCalendar,
} from 'react-icons/fa';
import axios from 'axios';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const API_BASE = 'http://34.206.193.218:4001/api/admin';

const ScheduleBidEstimate = () => {
  const { taskId } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [scheduleTasks, setScheduleTasks] = useState([]);
  const [bidTaskInfo, setBidTaskInfo] = useState(null);
  // Removed projectInfo state since we can't fetch it
  
  const token = localStorage.getItem('adminToken') || localStorage.getItem('token');

  const fetchScheduleTasks = async () => {
    try {
      setLoading(true);
      const res = await axios.get(
        `${API_BASE}/data/getScheduleTasksByBidTask/${taskId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (res.data.success) {
        setScheduleTasks(res.data.scheduleTasks || []);
        
        if (res.data.scheduleTasks && res.data.scheduleTasks.length > 0) {
          const task = res.data.scheduleTasks[0];
          setBidTaskInfo(task.bidTaskId);
          // Removed project info fetching since API doesn't exist
        } else {
          fetchBidTaskInfo();
        }
      }
    } catch (error) {
      console.error('Fetch schedule tasks error:', error);
      toast.error('Failed to load schedule tasks');
    } finally {
      setLoading(false);
    }
  };

  const fetchBidTaskInfo = async () => {
    try {
      const res = await axios.get(
        `${API_BASE}/data/getBidTaskById/${taskId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (res.data.success) {
        setBidTaskInfo(res.data.bidTask);
        // Removed project info fetching since API doesn't exist
      }
    } catch (error) {
      console.error('Fetch bid task info error:', error);
    }
  };

  useEffect(() => {
    if (taskId) {
      fetchScheduleTasks();
    }
  }, [taskId]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'Not set';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusBadge = (scheduledDate, installedDate) => {
    const today = new Date();
    const scheduled = new Date(scheduledDate);
    const installed = installedDate ? new Date(installedDate) : null;

    if (installed) {
      return <Badge bg="success" className="px-3 py-2">Completed</Badge>;
    } else if (scheduled < today) {
      return <Badge bg="danger" className="px-3 py-2">Overdue</Badge>;
    } else if (scheduled.toDateString() === today.toDateString()) {
      return <Badge bg="warning" text="dark" className="px-3 py-2">Today</Badge>;
    } else if (scheduled < new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000)) {
      return <Badge bg="info" className="px-3 py-2">Upcoming</Badge>;
    } else {
      return <Badge bg="secondary" className="px-3 py-2">Scheduled</Badge>;
    }
  };

  const calculateProgress = () => {
    if (scheduleTasks.length === 0) return 0;
    const completed = scheduleTasks.filter(task => task.installedDate).length;
    return Math.round((completed / scheduleTasks.length) * 100);
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <Spinner animation="border" variant="primary" />
        <span className="ms-3">Loading schedule information...</span>
      </div>
    );
  }

  const progress = calculateProgress();
  const completedCount = scheduleTasks.filter(t => t.installedDate).length;
  const upcomingCount = scheduleTasks.filter(t => !t.installedDate && new Date(t.scheduledDate) >= new Date()).length;
  const overdueCount = scheduleTasks.filter(t => !t.installedDate && new Date(t.scheduledDate) < new Date()).length;

  return (
    <Container fluid className="py-4" style={{ backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
      <ToastContainer position="top-right" autoClose={3000} />

      {/* HEADER */}
      <Card className="border-0 shadow-sm mb-4">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-start mb-3">
            <div>
              <Button
                variant="outline-primary"
                onClick={() => navigate(-1)}
                className="mb-3 d-flex align-items-center"
              >
                <FaArrowLeft className="me-2" />
                Back to Bid Estimates
              </Button>
              <h2 className="fw-bold mb-2">
                <FaCalendarAlt className="me-3" />
                Task Schedule Details
              </h2>
              
              {bidTaskInfo && (
                <div className="mb-3">
                  <h4 className="mb-2">
                    <FaClipboardCheck className="me-2 text-primary" />
                    {bidTaskInfo.taskName?.taskName || 'Unknown Task'}
                  </h4>
                  <div className="d-flex flex-wrap gap-3 align-items-center">
                    <Badge bg="info" className="px-3 py-2">
                      <FaDollarSign className="me-2" />
                      {formatCurrency(bidTaskInfo.totalAmount || 0)}
                    </Badge>
                    <Badge bg="light" text="dark" className="px-3 py-2">
                      <FaRulerCombined className="me-2" />
                      {bidTaskInfo.totalSqft || 0} sqft
                    </Badge>
                    <Badge bg="secondary" className="px-3 py-2">
                      <FaHardHat className="me-2" />
                      Task ID: {taskId?.substring(0, 12)}...
                    </Badge>
                  </div>
                </div>
              )}
            </div>
            
            <div className="d-flex gap-2">
              <Button
                variant="primary"
                onClick={fetchScheduleTasks}
                className="d-flex align-items-center"
              >
                <FaSync className="me-2" />
                Refresh
              </Button>
            </div>
          </div>
        </Card.Body>
      </Card>

      {scheduleTasks.length === 0 ? (
        <Card className="border-0 shadow-sm">
          <Card.Body className="text-center py-5">
            <FaCalendarAlt size={64} className="text-muted mb-3" />
            <h4 className="mb-3">No Schedule Tasks Found</h4>
            <p className="text-muted mb-4">
              No schedule tasks have been created for this bid task yet.
            </p>
          </Card.Body>
        </Card>
      ) : (
        <>
          {/* SCHEDULE OVERVIEW */}
          <Card className="border-0 shadow-sm mb-4">
            <Card.Header className="bg-light">
              <h4 className="mb-0">
                <FaCalendar className="me-2" />
                Schedule Overview
              </h4>
            </Card.Header>
            <Card.Body>
              <Row className="text-center">
                <Col md={3} sm={6} className="mb-4">
                  <Card className="border-0 bg-primary bg-opacity-10 h-100">
                    <Card.Body>
                      <div className="fw-bold display-6 mb-2">{scheduleTasks.length}</div>
                      <div className="text-muted">Total Schedule</div>
                    </Card.Body>
                  </Card>
                </Col>
                
                <Col md={3} sm={6} className="mb-4">
                  <Card className="border-0 bg-success bg-opacity-10 h-100">
                    <Card.Body>
                      <div className="fw-bold display-6 mb-2 text-success">{completedCount}</div>
                      <div className="text-muted">Completed</div>
                    </Card.Body>
                  </Card>
                </Col>
                
                <Col md={3} sm={6} className="mb-4">
                  <Card className="border-0 bg-warning bg-opacity-10 h-100">
                    <Card.Body>
                      <div className="fw-bold display-6 mb-2 text-warning">{upcomingCount}</div>
                      <div className="text-muted">Upcoming</div>
                    </Card.Body>
                  </Card>
                </Col>
                
                <Col md={3} sm={6} className="mb-4">
                  <Card className="border-0 bg-danger bg-opacity-10 h-100">
                    <Card.Body>
                      <div className="fw-bold display-6 mb-2 text-danger">{overdueCount}</div>
                      <div className="text-muted">Overdue</div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              {/* Progress Section */}
              <div className="mt-4">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h6 className="mb-0">
                    <FaChartLine className="me-2" />
                    Overall Progress
                  </h6>
                  <Badge bg={progress === 100 ? 'success' : progress >= 50 ? 'info' : 'warning'}>
                    {progress}% Complete
                  </Badge>
                </div>
                <ProgressBar 
                  now={progress} 
                  variant={progress === 100 ? 'success' : progress >= 50 ? 'info' : 'warning'}
                  style={{ height: '12px', borderRadius: '6px' }}
                  className="mb-0"
                />
              </div>
            </Card.Body>
          </Card>

          {/* INDIVIDUAL SCHEDULE CARDS */}
          {scheduleTasks.map((schedule, index) => (
            <Card key={schedule._id} className="border-0 shadow-sm mb-4">
              <Card.Header className="bg-light">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <div className="d-flex align-items-center mb-1">
                      <FaHardHat className="me-2 text-primary" size={20} />
                      <h4 className="fw-bold mb-0">{schedule.subcontractorName || 'Unnamed Subcontractor'}</h4>
                    </div>
                    <small className="text-muted">
                      <FaFileAlt className="me-1" />
                      Schedule ID: {schedule._id?.substring(0, 12)}...
                    </small>
                  </div>
                  <div>
                    {getStatusBadge(schedule.scheduledDate, schedule.installedDate)}
                  </div>
                </div>
              </Card.Header>
              
              <Card.Body>
                <Row>
                  {/* Left Column - Contact & Dates */}
                  <Col md={6}>
                    {/* Contact Information */}
                    <div className="mb-4">
                      <h5 className="mb-3 border-bottom pb-2">
                        <FaUser className="me-2" />
                        Contact Information
                      </h5>
                      <ListGroup variant="flush">
                        <ListGroup.Item className="border-0 px-0 py-2">
                          <div className="d-flex">
                            <div className="flex-shrink-0">
                              <FaEnvelope className="text-muted me-3" />
                            </div>
                            <div className="flex-grow-1">
                              <small className="text-muted d-block">Email</small>
                              <div className="fw-medium">{schedule.email || 'Not provided'}</div>
                            </div>
                          </div>
                        </ListGroup.Item>
                        <ListGroup.Item className="border-0 px-0 py-2">
                          <div className="d-flex">
                            <div className="flex-shrink-0">
                              <FaPhone className="text-muted me-3" />
                            </div>
                            <div className="flex-grow-1">
                              <small className="text-muted d-block">Phone</small>
                              <div className="fw-medium">{schedule.phone || 'Not provided'}</div>
                            </div>
                          </div>
                        </ListGroup.Item>
                      </ListGroup>
                    </div>

                    {/* Schedule Dates */}
                    <div className="mb-4">
                      <h5 className="mb-3 border-bottom pb-2">
                        <FaCalendarAlt className="me-2" />
                        Schedule Dates
                      </h5>
                      <ListGroup variant="flush">
                        <ListGroup.Item className="border-0 px-0 py-2">
                          <div className="d-flex">
                            <div className="flex-shrink-0">
                              <FaCalendarDay className="text-primary me-3" />
                            </div>
                            <div className="flex-grow-1">
                              <small className="text-muted d-block">Scheduled Date</small>
                              <div className="fw-medium">{formatDate(schedule.scheduledDate)}</div>
                            </div>
                          </div>
                        </ListGroup.Item>
                        <ListGroup.Item className="border-0 px-0 py-2">
                          <div className="d-flex">
                            <div className="flex-shrink-0">
                              <FaCalendarCheck className={`me-3 ${schedule.installedDate ? 'text-success' : 'text-secondary'}`} />
                            </div>
                            <div className="flex-grow-1">
                              <small className="text-muted d-block">Installed Date</small>
                              <div className={`fw-medium ${schedule.installedDate ? 'text-success' : ''}`}>
                                {schedule.installedDate ? formatDate(schedule.installedDate) : 'Not installed yet'}
                              </div>
                            </div>
                          </div>
                        </ListGroup.Item>
                      </ListGroup>
                    </div>
                  </Col>

                  {/* Right Column - Documents & Notes */}
                  <Col md={6}>
                    {/* Documents & Insurance */}
                    <div className="mb-4">
                      <h5 className="mb-3 border-bottom pb-2">
                        <FaFileContract className="me-2" />
                        Documents & Insurance
                      </h5>
                      <ListGroup variant="flush">
                        <ListGroup.Item className="border-0 px-0 py-2">
                          <div className="d-flex justify-content-between align-items-center">
                            <div>
                              <small className="text-muted d-block">Workers Compensation</small>
                            </div>
                            <div>
                              {schedule.workersCompensation ? (
                                <Badge bg="success" className="px-3 py-1">Provided</Badge>
                              ) : (
                                <Badge bg="warning" text="dark" className="px-3 py-1">Not Provided</Badge>
                              )}
                            </div>
                          </div>
                        </ListGroup.Item>
                        <ListGroup.Item className="border-0 px-0 py-2">
                          <div className="d-flex justify-content-between align-items-center">
                            <div>
                              <small className="text-muted d-block">Liability Insurance</small>
                            </div>
                            <div>
                              {schedule.liabilityInsurance ? (
                                <Badge bg="success" className="px-3 py-1">Provided</Badge>
                              ) : (
                                <Badge bg="warning" text="dark" className="px-3 py-1">Not Provided</Badge>
                              )}
                            </div>
                          </div>
                        </ListGroup.Item>
                        <ListGroup.Item className="border-0 px-0 py-2">
                          <div className="d-flex justify-content-between align-items-center">
                            <div>
                              <small className="text-muted d-block">Agreement Signed</small>
                            </div>
                            <div>
                              {schedule.agreementSigned ? (
                                <Badge bg="success" className="px-3 py-1">
                                  <FaCheckCircle className="me-1" />
                                  Signed
                                </Badge>
                              ) : (
                                <Badge bg="danger" className="px-3 py-1">
                                  <FaTimesCircle className="me-1" />
                                  Not Signed
                                </Badge>
                              )}
                            </div>
                          </div>
                        </ListGroup.Item>
                      </ListGroup>
                    </div>

                    {/* Notes */}
                    {schedule.notes && (
                      <div className="mb-4">
                        <h5 className="mb-3 border-bottom pb-2">
                          <FaNotesMedical className="me-2" />
                          Notes
                        </h5>
                        <Alert variant="light" className="mb-0">
                          <div className="p-3 bg-white rounded border">
                            <p className="mb-0">{schedule.notes}</p>
                          </div>
                        </Alert>
                      </div>
                    )}

                    {/* Created Information */}
                    <div className="mt-4 pt-3 border-top">
                      <h6 className="text-muted mb-3">Created Information</h6>
                      <div className="d-flex align-items-center">
                        <div className="flex-shrink-0">
                          <div className="bg-primary bg-opacity-10 rounded-circle p-2">
                            <FaUser className="text-primary" />
                          </div>
                        </div>
                        <div className="flex-grow-1 ms-3">
                          <div className="fw-medium">{schedule.createdBy?.name || 'Unknown'}</div>
                          <small className="text-muted">
                            <FaClock className="me-1" />
                            {formatDateTime(schedule.createdAt)}
                          </small>
                        </div>
                      </div>
                    </div>
                  </Col>
                </Row>
              </Card.Body>
              
              <Card.Footer className="bg-white border-top">
                <div className="d-flex justify-content-between align-items-center">
                  <small className="text-muted">
                    Schedule #{index + 1} of {scheduleTasks.length}
                  </small>
                  <div>
                    <Badge bg="light" text="dark" className="px-3 py-1">
                      <FaFileAlt className="me-1" />
                      ID: {schedule._id?.substring(0, 8)}...
                    </Badge>
                  </div>
                </div>
              </Card.Footer>
            </Card>
          ))}
        </>
      )}

      {/* EMPTY STATE */}
      {scheduleTasks.length === 0 && (
        <Card className="border-0 shadow-sm mt-4">
          <Card.Body>
            <Alert variant="info" className="mb-0">
              <div className="d-flex align-items-center">
                <FaInfoCircle className="me-3" size={24} />
                <div>
                  <h5 className="alert-heading mb-2">Schedule Management</h5>
                  <p className="mb-0">
                    This page displays all schedule tasks associated with the selected bid task.
                    Currently, there are no schedule tasks created. Schedule tasks would typically 
                    include subcontractor information, scheduled dates, and completion status.
                  </p>
                </div>
              </div>
            </Alert>
          </Card.Body>
        </Card>
      )}
    </Container>
  );
};

export default ScheduleBidEstimate;